import { useMemo } from "react";
import { useHttp } from "../http/useHttp";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

type InvoiceDetailsOperation =
  paths["/dashboard/contracts/{contract_shared_uuid}/invoices/{energy_date}/details"]["post"];
type InvoiceDetailsPath = InvoiceDetailsOperation["parameters"]["path"];

export interface ScalarData {
  value: string;
  descriptors: Record<string, string>;
  numeric_descriptors: Record<string, string>;
}

export interface IntervalData {
  values: Array<string | null>;
  descriptors: Record<string, string>;
  numeric_descriptors: Record<string, string>;
}

export interface InvoiceData {
  scalars: Record<string, ScalarData>;
  intervals: Record<string, IntervalData>;
}

export interface UseInvoiceDetailsInput {
  contract_shared_uuid: InvoiceDetailsPath["contract_shared_uuid"] | undefined;
  energy_date: InvoiceDetailsPath["energy_date"];
}

export const useInvoiceDetails = (
  input: UseInvoiceDetailsInput,
  options: Omit<UseAxiosQueryOptions<InvoiceData>, "queryKey" | "queryFn"> = {},
) => {
  const { client } = useHttp();

  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<InvoiceData>({
      queryKey: ["contract-invoice-details", input],
      queryFn: ({ signal }) =>
        client.post(
          `/contracts/${input.contract_shared_uuid}/invoices/${input.energy_date}/details`,
          undefined,
          { signal },
        ),
      enabled: options.enabled !== false && !!input?.contract_shared_uuid && !!input?.energy_date,
      ...options,
    });

  const { scalars, intervals } = useMemo(() => {
    if (!data?.data) {
      return { scalars: {} as InvoiceData["scalars"], intervals: {} as InvoiceData["intervals"] };
    }
    return {
      scalars: data.data.scalars,
      intervals: data.data.intervals,
    };
  }, [data]);

  useDefaultError(error);

  return {
    invoiceDetails: data,
    scalars,
    intervals,
    error,
    isLoading,
    isFetching,
    isPending,
    status,
    refetch,
  };
};

export type UseInvoiceDetailsReturn = ReturnType<typeof useInvoiceDetails>;
